import { all, call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';

import {
  ACTIVATE_CREDIT,
  GET_CREDITS,
  SCHEDULE_CREDIT
} from 'actions/credits/constants';
import {
  activateCreditSuccess,
  activateCreditError,
  getCreditsSuccess,
  getCreditsError,
  scheduleCreditSuccess,
  scheduleCreditError
} from 'actions/credits';
import { hideModal } from 'actions/modal';

import normaliseError from 'utils/normaliseError';

import { API_URL, HTTP_VERBS } from 'utils/constants';

/* Project credits */
export function* getCreditsSaga({ projectId, callback = () => {} }) {
  const requestUrl = `${API_URL}/projects/${projectId}/credits`;
  const opts = {
    method: HTTP_VERBS.GET,
    url: requestUrl
  };

  try {
    const response = yield call(axios, opts);
    yield put(getCreditsSuccess(projectId, response.data.data));
    yield call(callback);
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(getCreditsError(projectId, transformedError));
  }
}

export function* getCreditsWatcher() {
  yield takeLatest(GET_CREDITS, getCreditsSaga);
}

/* Activate credit */
export function* activateCreditSaga({
  projectId,
  instanceId,
  creditId,
  actions
}) {
  const requestUrl = `${API_URL}/projects/${projectId}/credits/${creditId}/activate`;

  const opts = {
    method: HTTP_VERBS.PUT,
    url: requestUrl,
    data: JSON.stringify({ instance_id: instanceId })
  };

  const errorText = (httpStatus) => {
    switch (httpStatus) {
      case 409:
        return 'Activation time clashes with an existing credit on this FlexBox.';
      default:
        return 'There was an error with the service: your credit was not activated.';
    }
  };

  try {
    const response = yield call(axios, opts);
    yield put(
      activateCreditSuccess(projectId, instanceId, creditId, response.data.data)
    );
    yield put(hideModal());
    yield call(toastr.success, 'Credit activated');
  } catch (error) {
    const transformedError = normaliseError(error);

    const { status } = transformedError;
    transformedError.description = errorText(status);

    yield put(
      activateCreditError(projectId, instanceId, creditId, transformedError)
    );

    if (actions) {
      yield call(actions.setErrors, {
        description: transformedError.description
      });
      yield call(actions.setSubmitting, false);
    }
  }
}

export function* activateCreditWatcher() {
  yield takeLatest(ACTIVATE_CREDIT, activateCreditSaga);
}

/* Schedule credit */
export function* scheduleCreditSaga({
  projectId,
  instanceId,
  creditId,
  scheduleDate,
  actions
}) {
  const isScheduling = !!scheduleDate;

  const requestUrl = `${API_URL}/projects/${projectId}/credits/${creditId}`;

  const opts = {
    method: HTTP_VERBS.PUT,
    url: requestUrl,
    data: JSON.stringify({
      // If unscheduling, explicitly pass null to API
      activation_time: isScheduling ? scheduleDate : null,
      // If unscheduling, do not pass instance_id to API
      ...(isScheduling && {
        instance_id: instanceId
      })
    })
  };

  const errorText = (httpStatus) => {
    switch (httpStatus) {
      case 409:
        return 'Activation time clashes with another scheduled credit on this FlexBox.';
      default:
        return 'There was an error with the service: your credit was not scheduled.';
    }
  };

  try {
    const response = yield call(axios, opts);
    yield put(
      scheduleCreditSuccess(projectId, instanceId, creditId, response.data.data)
    );
    if (isScheduling) {
      yield put(hideModal());
    }
    yield call(
      toastr.success,
      `Your credit has been ${isScheduling ? 'scheduled' : 'unscheduled'}`
    );
  } catch (error) {
    const transformedError = normaliseError(error);

    const { status } = transformedError;
    transformedError.description = errorText(status);

    yield put(
      scheduleCreditError(projectId, instanceId, creditId, transformedError)
    );

    if (!isScheduling) {
      yield call(toastr.error, 'There was a problem unscheduling this credit');
    }

    if (actions) {
      yield call(actions.setErrors, {
        description: transformedError.description
      });
      yield call(actions.setSubmitting, false);
    }
  }
}

export function* scheduleCreditWatcher() {
  yield takeLatest(SCHEDULE_CREDIT, scheduleCreditSaga);
}

export default function* rootSaga() {
  yield all([
    call(getCreditsWatcher),
    call(activateCreditWatcher),
    call(scheduleCreditWatcher)
  ]);
}
